import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { useTranslation } from "react-i18next";
import api from "../../../../../api/client";

// Helper to get last 6 days as YYYY-MM-DD
function getLast6Days() {
  const days = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push(d.toISOString().slice(0, 10));
  }
  return days;
}

// Helper to get last 6 months as YYYY-MM
function getLast6Months() {
  const months = [];
  const d = new Date();
  // Always set to first of month to avoid month rollover issues
  for (let i = 6; i >= 0; i--) {
    const dt = new Date(d.getFullYear(), d.getMonth()+1, 0);
    dt.setMonth(dt.getMonth() - i);
    const m = dt.toISOString().slice(0, 7);
    months.push(m);
  }
  return months;
}

const LeftSection = ({ selectedCompany }) => {
  const { t } = useTranslation();
  const [chartOptions, setChartOptions] = useState({
    chart: {
      id: "basic-bar",
    },
    xaxis: {
      categories: [],
    },
  });

  const [seriesData, setSeriesData] = useState([
    {
      name: t("dashboard.creditUsage"),
      data: [],
    },
  ]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewType, setViewType] = useState("date"); // "date" or "month"

  useEffect(() => {
    setSeriesData(prev => prev.map(series => ({ ...series, name: t("dashboard.creditUsage") })));
  }, [t]);

  useEffect(() => {
    fetchCreditUsageData(viewType);
    // eslint-disable-next-line
  }, [viewType, selectedCompany]);

  const fetchCreditUsageData = async (type) => {
    setLoading(true);
    setError(null);
    try {
      // Get admin user from localStorage
      const storedAdminUser = localStorage.getItem('adminUser');
      let company = null;
      if (storedAdminUser) {
        const adminUser = JSON.parse(storedAdminUser);
        company = adminUser?.company;
      }
      // Choose endpoint
      let endpoint = '';
      let labelKey = '';
      let fillLabels = [];
      if (type === 'month') {
        endpoint = 'observability/credits/monthly_usage';
        labelKey = 'month';
        fillLabels = getLast6Months();
      } else {
        endpoint = 'observability/credits/usage_patterns/';
        labelKey = 'date';
        fillLabels = getLast6Days();
      }
      // Fetch usage patterns
      const res = await api.get(endpoint);
      const data = res.data;
      
      // Find the company key dynamically
      let usageArr = [];
      const role = localStorage.getItem('role');
      
      if (role === 'Super Admin') {
        const isAllCompanies = !selectedCompany || selectedCompany === t('dashboard.allCompanies');
        if (!isAllCompanies) {
          usageArr = data[selectedCompany] || [];
        } else {
          // Aggregate usage of all companies
          const aggregatedMap = {};
          Object.keys(data).forEach(compKey => {
            const compData = data[compKey] || [];
            compData.forEach(item => {
              const key = item[labelKey];
              aggregatedMap[key] = (aggregatedMap[key] || 0) + item.usage;
            });
          });
          usageArr = Object.keys(aggregatedMap).map(key => ({
            [labelKey]: key,
            usage: aggregatedMap[key]
          }));
        }
      } else {
        if (company && data[company]) {
          usageArr = data[company];
        } else if (Object.keys(data).length > 0) {
          // fallback: use the first key if company not found
          usageArr = data[Object.keys(data)[0]];
        }
      }
      // Map label to usage
      const usageMap = {};
      usageArr.forEach(item => {
        usageMap[item[labelKey]] = item.usage;
      });
      // Fill missing labels with 0
      const categories = fillLabels;
      const usageData = fillLabels.map(label => usageMap[label] || 0);
      setChartOptions(prev => ({
        ...prev,
        xaxis: { ...prev.xaxis, categories },
      }));
      setSeriesData([
        {
          name: t("dashboard.creditUsage"),
          data: usageData,
        },
      ]);
    } catch (err) {
      setError(t("dashboard.creditUsageError"));
      setChartOptions(prev => ({ ...prev, xaxis: { ...prev.xaxis, categories: [] } }));
      setSeriesData([{ name: t("dashboard.creditUsage"), data: [] }]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewTypeChange = (e) => {
    setViewType(e.target.value);
  };

  return (
    <div className="w-full">
      <div className="flex flex-col items-start mb-2">
        <div className="relative">
          <select
            value={viewType}
            onChange={handleViewTypeChange}
            className="px-2 py-1  bg-gray-50 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-24"
          >
            <option value="date">{t("dashboard.byDates")}</option>
            <option value="month">{t("dashboard.byMonth")}</option>
          </select>
        </div>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center h-48 text-gray-500 ">
          {t("common.loading")}
        </div>
      ) : error ? (
        <div className="flex justify-center items-center h-48 text-red-500 ">
          {error}
        </div>
      ) : (
        <Chart
          options={chartOptions}
          series={seriesData}
          type="bar"
          width="100%"
          height={window.innerWidth < 600 ? "180" : window.innerWidth < 900 ? "200" : "220"}
        />
      )}
    </div>
  );
};

export default LeftSection;